import { NODE_HEIGHT_PX, TreeBrowserComponent, useTreeBrowser } from "@iventis/tree-browser";
import { useMaxElementCount, useWindowSize } from "@iventis/utilities";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import React, { Fragment, FunctionComponent, MutableRefObject, useMemo, useRef } from "react";
import { styled, Body2, Header2, customMediaQueryMax, fontSizes, formPadding, modalPadding, screenSizeBreakpoints } from "@iventis/styles";
import { Theme } from "@emotion/react";

import { SitemapRootNode, SitemapTreeBrowserContext } from "./sitemap-types";
import { SkeletonNode, sitemapNodeComponents } from "./sitemap-nodes";

export const SitemapTreeBrowser: FunctionComponent<{ addSitemapButtonRef?: MutableRefObject<HTMLButtonElement> }> = ({ addSitemapButtonRef }) => {
    const translate = useIventisTranslate();
    // Set the width to 1 since it doesnt matter, we just want 1 column
    const [containerRef, elementCount] = useMaxElementCount<HTMLDivElement>(1, NODE_HEIGHT_PX, { columns: 1 });

    const { screenHeight, screenWidth } = useWindowSize();

    const [{ expandedNodeIds, tree, isLoadingTree }] = useTreeBrowser(SitemapTreeBrowserContext, null, "expandedNodeIds", "tree", "isLoadingTree");

    const emptyMessageRef = useRef<HTMLDivElement>();

    const lineD = useMemo(() => {
        if (emptyMessageRef?.current?.getBoundingClientRect() == null || addSitemapButtonRef?.current?.getBoundingClientRect() == null) {
            return "M 0 0";
        }
        // Get coordinates
        const { x: p2x, y: p2y, width: p2w, height: p2h } = emptyMessageRef?.current?.getBoundingClientRect();
        const { x: p1x, y: p1y, width: p1w, height: p1h } = addSitemapButtonRef?.current?.getBoundingClientRect();

        // Calculate the center of the rects
        const p1xm = p1x + p1w * 0.5;
        const p1ym = p1y - p1h * 0.5;
        const p2xm = p2x + p2w * 0.5;
        const p2ym = p2y + p2h * 0.5;

        // distance of control point from mid-point of line:
        const xOffset = screenWidth > screenSizeBreakpoints.small ? screenWidth * 0.1 : screenWidth * 0.4;
        const yOffset = screenWidth > screenSizeBreakpoints.small ? 0 : screenHeight * 0.1;

        // location of control point, relate it to the bottom right of the screen:
        const c1x = screenWidth - xOffset;
        const c1y = screenHeight - yOffset;

        return `M${p1xm} ${p1ym} C ${screenWidth} ${c1y} ${c1x} ${c1y} ${p2xm} ${p2ym}`;
    }, [emptyMessageRef?.current?.getBoundingClientRect(), addSitemapButtonRef?.current?.getBoundingClientRect(), screenHeight, screenWidth]);

    const skeletons = useMemo(
        () =>
            new Array(elementCount).fill(true).map((_, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <Fragment key={`Sitemap-Skeleton-${i}`}>
                    <SkeletonNode index={i} total={elementCount} />
                </Fragment>
            )),
        [elementCount]
    );

    return (
        <SitemapTreeContainer ref={containerRef}>
            {isLoadingTree && skeletons}
            {!isLoadingTree && tree?.childCount > 0 && <TreeBrowserComponent<SitemapRootNode> node={tree} openNodes={expandedNodeIds} components={sitemapNodeComponents} />}
            {!isLoadingTree && (tree?.childCount === 0 || tree?.childCount == null) && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", height: "100%" }}>
                    <EmptySitemapContainer ref={emptyMessageRef}>
                        <StyledHeader style={{ textAlign: "center" }}>{translate(Content.settings3.sitemaps.no_sitemaps_title)}</StyledHeader>
                        <Body2 style={{ textAlign: "center" }}>
                            {translate(Content.settings3.sitemaps.no_sitemaps_body_1)} ‘<b>{translate(Content.settings3.sitemaps.add_sitemap)}</b>’{" "}
                            {translate(Content.settings3.sitemaps.no_sitemaps_body_2)}
                        </Body2>
                    </EmptySitemapContainer>
                    <LineDrawingContainer>
                        <svg style={{ width: "100%", height: "100%" }}>
                            <StyledLine d={lineD} />
                        </svg>
                    </LineDrawingContainer>
                </div>
            )}
        </SitemapTreeContainer>
    );
};

export const SitemapTreeContainer = styled.div`
    flex: 1 1;
    // Every version and level node defaults to have a border top, remove that border if the version node is immediately after a sitemap node
    .sitemap-node + .version-node {
        border-top: 0;
    }

    // Give top borders to every sitemap that isn't next to a sitemap
    .sitemap-node:nth-child(1) {
        border-top: solid 1px ${({ theme }: { theme: Theme }) => theme.shades.darkBorder};
    }
    .level-node + .sitemap-node {
        border-top: solid 1px ${({ theme }: { theme: Theme }) => theme.shades.darkBorder};
    }
    .version-node + .sitemap-node {
        border-top: solid 1px ${({ theme }: { theme: Theme }) => theme.shades.darkBorder};
    }
`;

const EmptySitemapContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 50px;
    border-radius: 10px;
    gap: 10px;
    max-width: 490px;
    /* Card shadows */
    box-shadow: 0px 7px 10px -5px ${({ theme }: { theme: Theme }) => theme.shades.two};
    background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};

    ${customMediaQueryMax(screenSizeBreakpoints.small)} {
        max-width: 230px;
        padding: ${formPadding} ${modalPadding};
    }
`;

const LineDrawingContainer = styled.div`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    z-index: -10;
`;

const StyledLine = styled.path`
    stroke-width: 1px;
    fill: transparent;
    stroke: ${({ theme }: { theme: Theme }) => theme.shades.two};
    stroke-dasharray: 15 10;
`;

const StyledHeader = styled(Header2)`
    ${customMediaQueryMax(screenSizeBreakpoints.small)} {
        color: ${({ color, theme }) => theme.typographyColors[color]};
        font-size: ${fontSizes.xLarge};
        font-weight: 600;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.2;
        letter-spacing: 0.15px;
    }
`;
